import React, { useCallback, useEffect, useRef, useState } from 'react';
import { isSupabaseConfigured, supabase } from '../supabaseClient';
import SmartExContent from './smartexContent';
import SmartEx from './smartex';
import Loading from './Loading';
import ExamResults from './result';
import ExamTransition from './ExamTransition';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';

/* ============================================================
   CONSTANTES
   Police : Outfit (géométrique, très affirmée en 800/900).
   Seules transform / opacity / height sont animées.
   ============================================================ */
const EASE = [0.22, 1, 0.36, 1];
const MAX_EXAM_CHAPTERS = 3;
const PREP_SECONDS = 6;

const FONT_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;700;800;900&display=swap');
.spx { font-family: 'Outfit', system-ui, -apple-system, 'Segoe UI', sans-serif; }
.spx button { -webkit-tap-highlight-color: transparent; font-family: inherit; }
.spx .pb-safe { padding-bottom: max(env(safe-area-inset-bottom), 1rem); }
`;

/* Bouton "3D" : le relief donne l'envie d'appuyer */
const BTN_BLUE = 'bg-[#4A90E2] text-white border-b-4 border-[#2F6FB8] active:border-b-0 active:translate-y-1';
const BTN_NAVY = 'bg-[#2B4360] text-white border-b-4 border-[#1A2B40] active:border-b-0 active:translate-y-1';
const BTN_GREEN = 'bg-[#7C9374] text-white border-b-4 border-[#5D7355] active:border-b-0 active:translate-y-1';

/* ============================================================
   HOOKS
   ============================================================ */
const useBodyLock = (active) => {
  useEffect(() => {
    if (!active) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [active]);
};

const useEscape = (active, handler) => {
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') handler(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, handler]);
};

/* ============================================================
   COMPOSANTS UI
   ============================================================ */
const ViewFade = ({ children }) => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.18 }}>
    {children}
  </motion.div>
);

// Déplie / replie son contenu en douceur, sur place
const Collapse = ({ open, children }) => (
  <AnimatePresence initial={false}>
    {open && (
      <motion.div
        key="content"
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: 'auto', opacity: 1 }}
        exit={{ height: 0, opacity: 0 }}
        transition={{ duration: 0.32, ease: EASE }}
        style={{ overflow: 'hidden' }}
      >
        {children}
      </motion.div>
    )}
  </AnimatePresence>
);

// Chevron dessiné en CSS (aucune icône)
const Chevron = ({ open }) => (
  <span
    aria-hidden="true"
    className={`block w-2.5 h-2.5 border-r-[3px] border-b-[3px] border-current rounded-[1px] transition-transform duration-300 ${open ? '-rotate-[135deg] translate-y-[3px]' : 'rotate-45 -translate-y-[2px]'}`}
  />
);

const ExerciseRow = ({ exercise, index, onPick }) => (
  <motion.li
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.28, ease: EASE, delay: 0.05 + Math.min(index, 8) * 0.04 }}
    className="flex items-center gap-3 rounded-2xl bg-[#F4F7FB] p-3 pl-4"
  >
    <span className="w-7 text-lg font-black text-[#4A90E2] tabular-nums">{index + 1}</span>
    <div className="flex-1 min-w-0">
      <p className="text-[15px] font-bold text-[#1A2B40] leading-snug line-clamp-2">
        {exercise.title || `Exercice ${index + 1}`}
      </p>
      {exercise.estimated_time && (
        <p className="text-xs font-medium text-slate-500 mt-0.5">{exercise.estimated_time}</p>
      )}
    </div>
    <button
      type="button"
      onClick={() => onPick(exercise)}
      className={`${BTN_BLUE} shrink-0 min-h-[44px] px-5 rounded-xl text-sm font-extrabold transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1A2B40] focus-visible:ring-offset-2`}
    >
      Commencer
    </button>
  </motion.li>
);

const ChapterCard = ({
  chapter, index, isOpen, isExamMode, isSelected, selectionOrder,
  disabled, isBusy, exercises, loadingExercises, onSelect, onPick
}) => {
  const active = isOpen || isSelected;
  return (
    <motion.li
      layout="position"
      transition={{ duration: 0.32, ease: EASE }}
      className={`rounded-3xl border-2 transition-colors duration-200 ${
        isSelected ? 'border-[#7C9374] bg-[#F1F5EF]' : isOpen ? 'border-[#4A90E2] bg-white' : 'border-slate-200 bg-white'
      } ${disabled ? 'opacity-40' : ''}`}
    >
      <button
        type="button"
        onClick={() => onSelect(chapter)}
        disabled={disabled}
        aria-expanded={isExamMode ? undefined : isOpen}
        aria-pressed={isExamMode ? isSelected : undefined}
        className="w-full flex items-center gap-4 p-4 text-left rounded-3xl touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90E2] disabled:cursor-not-allowed"
      >
        <span
          className={`w-12 h-12 shrink-0 rounded-2xl flex items-center justify-center text-xl font-black tabular-nums transition-colors duration-200 ${
            active ? (isSelected ? 'bg-[#7C9374] text-white' : 'bg-[#4A90E2] text-white') : 'bg-[#2B4360]/[0.07] text-[#2B4360]'
          }`}
        >
          {index + 1}
        </span>

        <span className="flex-1 min-w-0">
          <span className="block text-[17px] font-extrabold text-[#1A2B40] leading-tight line-clamp-2">{chapter.name}</span>
          <span className="block text-xs font-medium text-slate-500 mt-1">
            {chapter.exerciseCount} exercice{chapter.exerciseCount > 1 ? 's' : ''}
          </span>
        </span>

        <span className="shrink-0 w-9 h-9 flex items-center justify-center text-slate-400">
          {isBusy ? (
            <span className="w-5 h-5 rounded-full border-[3px] border-slate-200 border-t-[#2B4360] animate-spin" />
          ) : isExamMode ? (
            <span
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black transition-colors duration-200 ${
                isSelected ? 'bg-[#7C9374] text-white' : 'border-2 border-dashed border-slate-300'
              }`}
              aria-label={isSelected ? `Sélectionné en position ${selectionOrder}` : undefined}
            >
              {isSelected ? selectionOrder : ''}
            </span>
          ) : (
            <span className={isOpen ? 'text-[#4A90E2]' : ''}><Chevron open={isOpen} /></span>
          )}
        </span>
      </button>

      {!isExamMode && (
        <Collapse open={isOpen}>
          <div className="px-4 pb-4">
            {loadingExercises ? (
              <div className="space-y-2" aria-busy="true">
                {[0, 1].map((k) => <div key={k} className="h-[64px] rounded-2xl bg-slate-100 animate-pulse" />)}
              </div>
            ) : exercises.length === 0 ? (
              <p className="py-6 text-center text-sm font-medium text-slate-500">
                Aucun exercice disponible pour ce chapitre.
              </p>
            ) : (
              <ul className="space-y-2">
                {exercises.map((ex, i) => (
                  <ExerciseRow key={ex.id} exercise={ex} index={i} onPick={onPick} />
                ))}
              </ul>
            )}
          </div>
        </Collapse>
      )}
    </motion.li>
  );
};

const ChapterSkeleton = () => (
  <li className="rounded-3xl border-2 border-slate-200 bg-white p-4 flex items-center gap-4 animate-pulse">
    <div className="w-12 h-12 rounded-2xl bg-slate-100 shrink-0" />
    <div className="flex-1 space-y-2">
      <div className="h-4 w-3/4 rounded-full bg-slate-100" />
      <div className="h-3 w-1/4 rounded-full bg-slate-100" />
    </div>
  </li>
);

const StateCard = ({ title, text, actionLabel, onAction, error }) => (
  <div className="rounded-3xl border-2 border-slate-200 bg-white p-8 text-center" role={error ? 'alert' : undefined}>
    <h3 className={`text-xl font-black ${error ? 'text-red-600' : 'text-[#1A2B40]'}`}>{title}</h3>
    <p className="text-sm text-slate-500 mt-2 leading-relaxed">{text}</p>
    {actionLabel && (
      <button
        type="button"
        onClick={onAction}
        className={`${BTN_NAVY} mt-6 min-h-[48px] px-7 rounded-2xl font-extrabold text-sm transition-transform touch-manipulation`}
      >
        {actionLabel}
      </button>
    )}
  </div>
);

// Sélecteur de mode : deux choix visibles en permanence
const ModeSwitch = ({ examMode, onChange }) => (
  <div role="tablist" aria-label="Mode de travail" className="relative mt-6 grid grid-cols-2 p-1 rounded-2xl bg-slate-200/70 max-w-md">
    <motion.span
      aria-hidden="true"
      className="absolute top-1 bottom-1 left-1 w-[calc(50%-4px)] rounded-xl bg-white shadow-sm"
      animate={{ x: examMode ? '100%' : '0%' }}
      transition={{ type: 'spring', damping: 30, stiffness: 400 }}
    />
    {[
      { id: false, label: 'Entraînement' },
      { id: true, label: 'Contrôle blanc' }
    ].map((m) => (
      <button
        key={m.label}
        type="button"
        role="tab"
        aria-selected={examMode === m.id}
        onClick={() => examMode !== m.id && onChange()}
        className={`relative z-10 min-h-[44px] rounded-xl text-sm font-extrabold transition-colors duration-200 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90E2] ${
          examMode === m.id ? 'text-[#1A2B40]' : 'text-slate-500'
        }`}
      >
        {m.label}
      </button>
    ))}
  </div>
);

/* Préparation : le compte à rebours vit ici, le parent ne se re-rend pas */
const PrepModal = ({ onLaunch, onCancel }) => {
  const [seconds, setSeconds] = useState(PREP_SECONDS);
  const launchRef = useRef(onLaunch);
  const firedRef = useRef(false);
  launchRef.current = onLaunch;

  useBodyLock(true);
  useEscape(true, onCancel);

  useEffect(() => {
    if (seconds <= 0) {
      if (!firedRef.current) { firedRef.current = true; launchRef.current(); }
      return undefined;
    }
    const id = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [seconds]);

  const C = 2 * Math.PI * 44;

  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label="Préparation de l'exercice"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-[#1A2B40]/90"
    >
      <motion.div
        initial={{ scale: 0.95, y: 16, opacity: 0 }} animate={{ scale: 1, y: 0, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
        transition={{ duration: 0.25, ease: EASE }}
        className="bg-white rounded-[32px] p-8 max-w-sm w-full text-center"
      >
        <div className="relative w-28 h-28 mx-auto mb-6" role="timer" aria-live="off">
          <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100" aria-hidden="true">
            <circle cx="50" cy="50" r="44" fill="none" className="stroke-slate-100" strokeWidth="8" />
            <circle
              cx="50" cy="50" r="44" fill="none" stroke="#4A90E2" strokeWidth="8" strokeLinecap="round"
              strokeDasharray={C}
              strokeDashoffset={C * (1 - seconds / PREP_SECONDS)}
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />
          </svg>
          <span className="absolute inset-0 flex items-center justify-center text-5xl font-black text-[#1A2B40] tabular-nums">
            {seconds}
          </span>
        </div>

        <h3 className="text-2xl font-black text-[#1A2B40] mb-2">Prépare ton matériel</h3>
        <p className="text-slate-500 text-[15px] font-medium mb-7 leading-relaxed">
          Une feuille blanche et un stylo, c'est tout ce qu'il te faut. L'exercice démarre dans quelques secondes.
        </p>

        <button
          type="button"
          onClick={() => setSeconds(0)}
          className={`${BTN_BLUE} w-full min-h-[52px] rounded-2xl font-extrabold transition-transform touch-manipulation`}
        >
          Je suis prêt
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="w-full min-h-[48px] mt-2 text-slate-500 text-sm font-bold active:text-slate-800 touch-manipulation"
        >
          Annuler
        </button>
      </motion.div>
    </motion.div>
  );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
const SmartExChapInner = ({ onLogout }) => {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [className, setClassName] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [currentView, setCurrentView] = useState('chapters');
  const [loadingChapter, setLoadingChapter] = useState(null);
  const [studentName, setStudentName] = useState('Étudiant');

  const [examMode, setExamMode] = useState(false);
  const [selectedExercises, setSelectedExercises] = useState([]);
  const [examStarted, setExamStarted] = useState(false);
  const [currentExamExercise, setCurrentExamExercise] = useState(0);
  const [examExercises, setExamExercises] = useState([]);
  const [examResults, setExamResults] = useState([]);
  const [showExamResults, setShowExamResults] = useState(false);
  const [showExamTransition, setShowExamTransition] = useState(false);
  const [currentTransitionData, setCurrentTransitionData] = useState(null);

  // Chapitre déplié + exercices par chapitre (le cache EST l'état : réouverture instantanée)
  const [openChapter, setOpenChapter] = useState(null);
  const [exercisesByChapter, setExercisesByChapter] = useState({});
  const [loadingExercisesFor, setLoadingExercisesFor] = useState(null);

  const [pendingExercise, setPendingExercise] = useState(null);
  const [contentKey, setContentKey] = useState(0);
  const startTimeoutRef = useRef(null);

  useEffect(() => () => clearTimeout(startTimeoutRef.current), []);

  useEffect(() => {
    const userInfo = JSON.parse(sessionStorage.getItem('user_info') || '{}');
    if (userInfo.firstName) setStudentName(userInfo.firstName);
    if (userInfo.level) setClassName(userInfo.level);
    else {
      setError('Impossible de déterminer votre niveau. Veuillez vous reconnecter.');
      setLoading(false);
    }
  }, []);

  const fetchChapters = useCallback(async () => {
    if (!className) return;
    setLoading(true);
    setError(null);
    setExercisesByChapter({});
    try {
      if (!isSupabaseConfigured()) throw new Error('Supabase non configuré.');
      const { data, error: fetchError } = await supabase
        .from('smartex')
        .select('chapter, title, estimated_time')
        .eq('class_level', className)
        .eq('is_active', true);

      if (fetchError) throw fetchError;

      const chaptersMap = {};
      data.forEach((exercise) => {
        const chapterName = exercise.chapter;
        if (!chaptersMap[chapterName]) {
          chaptersMap[chapterName] = { name: chapterName, exerciseCount: 0, exercises: [] };
        }
        chaptersMap[chapterName].exerciseCount++;
      });

      setChapters(Object.values(chaptersMap).map((chapter, index) => ({ ...chapter, id: index + 1 })));
    } catch (err) {
      setError(err?.message || 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  }, [className]);

  useEffect(() => { fetchChapters(); }, [fetchChapters]);

  const toggleExamMode = () => {
    const newMode = !examMode;
    setExamMode(newMode);
    if (!newMode) setSelectedExercises([]);
    setOpenChapter(null);
    setExamStarted(false);
  };

  // Déplie un chapitre ; charge ses exercices une seule fois
  const toggleChapter = async (chapter) => {
    if (openChapter === chapter.name) {
      setOpenChapter(null);
      return;
    }
    setOpenChapter(chapter.name);
    if (exercisesByChapter[chapter.name]) return;

    setLoadingExercisesFor(chapter.name);
    try {
      const { data } = await supabase
        .from('smartex').select('*')
        .eq('chapter', chapter.name).eq('class_level', className).eq('is_active', true);
      if (data) setExercisesByChapter((prev) => ({ ...prev, [chapter.name]: data }));
    } catch (err) {
      console.error('Erreur chargement', err);
    } finally {
      setLoadingExercisesFor((current) => (current === chapter.name ? null : current));
    }
  };

  const handleChapterSelection = async (chapter) => {
    if (loadingChapter) return;

    if (examMode) {
      const isAlreadySelected = selectedExercises.some((ex) => ex.chapterInfo.name === chapter.name);
      if (isAlreadySelected) {
        setSelectedExercises((prev) => prev.filter((ex) => ex.chapterInfo.name !== chapter.name));
        return;
      }
      if (selectedExercises.length >= MAX_EXAM_CHAPTERS) return;

      try {
        setLoadingChapter(chapter.name);
        const { data: randomExercise } = await supabase
          .from('smartex').select('*')
          .eq('chapter', chapter.name).eq('class_level', className).eq('is_active', true);

        if (randomExercise?.length > 0) {
          const randomIndex = Math.floor(Math.random() * randomExercise.length);
          const picked = {
            ...randomExercise[randomIndex],
            chapterInfo: chapter,
            selectionId: Date.now() + Math.random()
          };
          setSelectedExercises((prev) => [...prev, picked]);
        }
      } catch (err) {
        console.error('Erreur sélection', err);
      } finally {
        setLoadingChapter(null);
      }
      return;
    }
    toggleChapter(chapter);
  };

  const handleSelectSpecificExercise = (exercise) => setPendingExercise(exercise);

  const executeLaunchExercise = (exerciseData) => {
    setSelectedExercise(exerciseData);
    setContentKey((k) => k + 1);
    setCurrentView('content');
  };

  const handlePrepLaunch = useCallback(() => {
    setPendingExercise((current) => {
      if (current) setTimeout(() => executeLaunchExercise(current), 0);
      return null;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePrepCancel = useCallback(() => setPendingExercise(null), []);

  const startExam = async () => {
    if (selectedExercises.length !== MAX_EXAM_CHAPTERS) return;
    setExamExercises(selectedExercises);
    setExamStarted(true);
    setCurrentExamExercise(0);
    startTimeoutRef.current = setTimeout(() => {
      setSelectedExercise(selectedExercises[0]);
      setContentKey((k) => k + 1);
      setCurrentView('content');
    }, 500);
  };

  const handleAnalysisComplete = (data, isProgressive = false, name = null) => {
    setAnalysisData({ data, isProgressive, studentName: name || studentName });
    setCurrentView('loading');
  };

  const handleExamExerciseComplete = async (exerciseData) => {
    setExamResults((prev) => [...prev, exerciseData]);
    const isLastExercise = exerciseData.exerciseIndex === examExercises.length - 1;
    setCurrentTransitionData({
      currentExercise: exerciseData.exerciseIndex + 1,
      totalExercises: examExercises.length,
      completedExercise: { chapter: examExercises[exerciseData.exerciseIndex]?.chapter || 'Chapitre' },
      nextExercise: !isLastExercise ? { chapter: examExercises[exerciseData.exerciseIndex + 1]?.chapter } : null,
      isLastExercise, studentName, examExercises
    });
    setShowExamTransition(true);
    setCurrentView('exam_transition');
  };

  const handleBackToChapters = () => {
    setSelectedExercise(null);
    setAnalysisData(null);
    setCurrentView('chapters');
    setExamStarted(false);
  };

  /* ---------------- ROUTAGE INTERNE (inchangé) ---------------- */
  if (currentView === 'content' && selectedExercise) {
    return (
      <ViewFade key="content">
        <SmartExContent
          chapter={selectedExercise}
          key={`${selectedExercise.id}${examMode ? '_exam' : ''}_${contentKey}`}
          onBack={handleBackToChapters}
          onAnalysisComplete={handleAnalysisComplete}
          examMode={examMode && examStarted}
          currentExercise={currentExamExercise}
          totalExercises={examExercises.length}
          onExerciseComplete={handleExamExerciseComplete}
        />
      </ViewFade>
    );
  }
  if (currentView === 'loading' && analysisData) {
    return <ViewFade key="loading"><Loading analysisData={analysisData.data} onStartTransition={() => setCurrentView('results')} /></ViewFade>;
  }
  if (currentView === 'results' && analysisData) {
    return <ViewFade key="results"><SmartEx data={analysisData.data} onBack={handleBackToChapters} isProgressive={true} studentName={analysisData.studentName} /></ViewFade>;
  }
  if (currentView === 'exam_results' && showExamResults) {
    return <ViewFade key="exam_results"><ExamResults data={examResults} onBack={handleBackToChapters} /></ViewFade>;
  }
  if (currentView === 'exam_transition' && showExamTransition) {
    return (
      <ViewFade key="exam_transition">
        <ExamTransition
          {...currentTransitionData}
          onContinue={(polledData) => {
            if (currentTransitionData?.isLastExercise) {
              if (polledData) setExamResults(polledData);
              setShowExamResults(true);
              setCurrentView('exam_results');
            } else {
              const nextIndex = currentTransitionData.currentExercise;
              setCurrentExamExercise(nextIndex);
              setSelectedExercise(examExercises[nextIndex]);
              setContentKey((k) => k + 1);
              setCurrentView('content');
            }
            setShowExamTransition(false);
          }}
        />
      </ViewFade>
    );
  }

  /* ---------------- ÉCRAN CHAPITRES ---------------- */
  const examFull = selectedExercises.length >= MAX_EXAM_CHAPTERS;
  const remaining = MAX_EXAM_CHAPTERS - selectedExercises.length;
  const totalExercises = chapters.reduce((sum, c) => sum + (c.exerciseCount || 0), 0);

  return (
    <div className="spx min-h-[100dvh] w-full bg-[#F4F7FB] text-slate-900 relative">
      <style dangerouslySetInnerHTML={{ __html: FONT_CSS }} />

      <AnimatePresence>
        {pendingExercise && (
          <PrepModal key="prep" onLaunch={handlePrepLaunch} onCancel={handlePrepCancel} />
        )}
      </AnimatePresence>

      <div className="max-w-[720px] mx-auto px-5" style={{ paddingBottom: examMode ? 'calc(8rem + env(safe-area-inset-bottom))' : 'calc(3rem + env(safe-area-inset-bottom))' }}>
        {/* En-tête */}
        <header className="flex items-center justify-between" style={{ paddingTop: 'max(env(safe-area-inset-top), 1rem)' }}>
          <div className="flex items-center gap-3">
            <img
              src="https://res.cloudinary.com/do5zemcrk/image/upload/v1766679468/Gemini_Generated_Image_35upzx35upzx35up_1_farsag.png"
              alt="Spyrale"
              width="40" height="40"
              decoding="async"
              className="w-10 h-10 rounded-xl border-2 border-slate-200 bg-white object-cover"
            />
            <span className="text-xl font-black tracking-tight text-[#2B4360]">Spyrale</span>
            {className && (
              <span className="px-2.5 py-1 rounded-full bg-[#7C9374]/15 text-[#5D7355] text-xs font-extrabold">{className}</span>
            )}
          </div>
          <button
            type="button"
            onClick={onLogout}
            className="min-h-[44px] px-4 rounded-xl border-2 border-slate-200 bg-white text-sm font-bold text-slate-600 active:bg-slate-50 touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4A90E2]"
          >
            Déconnexion
          </button>
        </header>

        {/* Titre */}
        <section className="pt-10 pb-6">
          <p className="text-base font-bold text-slate-500">Bonjour {studentName}</p>
          <h1 className="mt-1 text-[40px] sm:text-5xl font-black text-[#1A2B40] tracking-tight leading-[1.05]">
            {examMode ? 'Choisis 3 chapitres pour ton contrôle.' : 'Quel chapitre travailles-tu aujourd\'hui ?'}
          </h1>
          <p className="mt-3 text-[15px] font-medium text-slate-500 max-w-md leading-relaxed">
            {examMode
              ? 'Un exercice est tiré au sort dans chaque chapitre, sans pouvoir le choisir.'
              : totalExercises > 0
                ? `${totalExercises} exercices répartis en ${chapters.length} chapitres. Touche un chapitre pour voir ses exercices.`
                : 'Touche un chapitre pour voir ses exercices.'}
          </p>
          <ModeSwitch examMode={examMode} onChange={toggleExamMode} />
        </section>

        {/* Liste */}
        <main>
          {loading ? (
            <ul className="space-y-3" aria-busy="true" aria-label="Chargement des chapitres">
              {[0, 1, 2, 3, 4].map((k) => <ChapterSkeleton key={k} />)}
            </ul>
          ) : error && chapters.length === 0 ? (
            <StateCard
              error
              title="Chargement impossible"
              text={error}
              actionLabel={className ? 'Réessayer' : undefined}
              onAction={fetchChapters}
            />
          ) : chapters.length === 0 ? (
            <StateCard
              title="Aucun chapitre pour le moment"
              text="Les chapitres de ton niveau apparaîtront ici dès qu'ils seront disponibles."
            />
          ) : (
            <ul className="space-y-3">
              {chapters.map((chapter, index) => {
                const selectedIndex = selectedExercises.findIndex((ex) => ex.chapterInfo.name === chapter.name);
                const isSelected = selectedIndex !== -1;
                return (
                  <ChapterCard
                    key={chapter.id}
                    chapter={chapter}
                    index={index}
                    isOpen={!examMode && openChapter === chapter.name}
                    isExamMode={examMode}
                    isSelected={isSelected}
                    selectionOrder={isSelected ? selectedIndex + 1 : null}
                    disabled={examMode && examFull && !isSelected}
                    isBusy={loadingChapter === chapter.name}
                    exercises={exercisesByChapter[chapter.name] || []}
                    loadingExercises={loadingExercisesFor === chapter.name && !exercisesByChapter[chapter.name]}
                    onSelect={handleChapterSelection}
                    onPick={handleSelectSpecificExercise}
                  />
                );
              })}
            </ul>
          )}
        </main>
      </div>

      {/* Barre du contrôle blanc */}
      <AnimatePresence>
        {examMode && (
          <motion.div
            initial={{ y: '130%' }} animate={{ y: 0 }} exit={{ y: '130%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 320 }}
            className="fixed inset-x-0 z-40 px-4 pointer-events-none"
            style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          >
            <div className="max-w-[720px] mx-auto bg-[#1A2B40] text-white rounded-3xl shadow-2xl p-3 pl-5 pointer-events-auto flex items-center justify-between gap-4">
              <div className="min-w-0" aria-live="polite">
                <div className="flex gap-1.5 mb-2" aria-hidden="true">
                  {Array.from({ length: MAX_EXAM_CHAPTERS }).map((_, i) => (
                    <span
                      key={i}
                      className={`h-2 w-9 rounded-full transition-colors duration-300 ${i < selectedExercises.length ? 'bg-[#7C9374]' : 'bg-white/15'}`}
                    />
                  ))}
                </div>
                <p className="text-sm font-extrabold leading-tight truncate">
                  {examFull ? 'Contrôle prêt' : `${selectedExercises.length} sur ${MAX_EXAM_CHAPTERS} chapitres`}
                </p>
                <p className="text-xs text-white/60 truncate">
                  {examFull ? 'Lance quand tu es prêt.' : `Encore ${remaining} à choisir`}
                </p>
              </div>

              <button
                type="button"
                onClick={startExam}
                disabled={!examFull || examStarted}
                className={`min-h-[52px] px-7 rounded-2xl font-extrabold text-sm shrink-0 transition-transform touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                  examFull && !examStarted ? BTN_GREEN : 'bg-white/10 text-white/40 border-b-4 border-transparent cursor-not-allowed'
                }`}
              >
                {examStarted ? 'Lancement' : 'Lancer le contrôle'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SmartExChap = (props) => (
  <MotionConfig reducedMotion="user">
    <SmartExChapInner {...props} />
  </MotionConfig>
);

export default SmartExChap;